import React, { memo, useCallback, useContext, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Typography, Menu, MenuItem } from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import AssignmentRoundedIcon from "@mui/icons-material/AssignmentRounded";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LanguageIcon from "@mui/icons-material/Language";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { logout } from "../../Login/userAuthActions";
import { RootState } from "../../store/store";
import HeaderStyles from "./HeaderStyles";
import { isFeatureAvailable } from "../../utils/Utility";
import i18n from "./../../translator/i18next";
import { useTranslation } from "react-i18next";
import { clearChat } from "../../store/user/insureAssist/insureAssistActions";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import DesktopWindowsOutlinedIcon from "@mui/icons-material/DesktopWindowsOutlined";
import RecordVoiceOverOutlinedIcon from "@mui/icons-material/RecordVoiceOverOutlined";
import ChatIcon from "@mui/icons-material/Chat";
import { toggleChat } from "../../store/common/chatUISlice";
 
interface HeaderProps {
  isInsureAssistOpen?: boolean;
  setIsInsureAssistOpen?: (isOpen: boolean) => void;
  showInsureAssist?: boolean;
  chatWidth?: number | null;
}
 
const Header = memo(
  ({
    isInsureAssistOpen,
    setIsInsureAssistOpen,
    showInsureAssist,
    chatWidth,
  }: HeaderProps) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const location = useLocation();
    const { uiSettings } = useAppSelector(
      (state: RootState) => state.uiSettings
    );
    const { userDetails } = useAppSelector(
      (state: RootState) => state.userAuth
    );
    const { features } = useAppSelector((state: RootState) => state.features);
 
    const [anchorEl, setAnchorEl] = useState<null | SVGSVGElement>(null);
    const [languageMenu, setLanguageMenu] = useState<null | HTMLElement>(null);
 
    const menuOpen = Boolean(anchorEl);
    const languageOpen = Boolean(languageMenu);
 
    const handleMenuClick = (event: React.MouseEvent<SVGSVGElement>) => {
      setAnchorEl(event.currentTarget);
    };
 
    const handleMenuClose = () => {
      setAnchorEl(null);
    };
 
    const handleLanguageClick = (event: React.MouseEvent<SVGSVGElement>) => {
      setLanguageMenu(event.currentTarget as unknown as HTMLElement);
    };
 
    const handleLanguageClose = () => {
      setLanguageMenu(null);
    };
 
    const handleLanguageSelect = (language: string) => {
      // dispatch(updateLanguage(language));
      if (i18n.language !== language) {
        i18n.changeLanguage(language);
        window.location.reload();
      }
      setLanguageMenu(null);
      if (userDetails && userDetails.customerId) {
        const requestParams: { userId: string; param: string } = {
          userId: userDetails.customerId,
          param: "all",
        };
        dispatch(clearChat(requestParams));
      }
    };
 
    const handleProfileClick = useCallback(() => {
      navigate("/customer/profile");
      setAnchorEl(null);
    }, [navigate]);
 
    const handleMyTasksClick = () => {
      navigate("/customer/service-requests");
      setAnchorEl(null);
    };
 
    const handleLogout = () => {
      if (userDetails && userDetails.customerId) {
        setAnchorEl(null);
        // Prevent previous chat/session data from lingering after logout.
        sessionStorage.removeItem("chatHeaderMessages");
        sessionStorage.removeItem("chatHeaderConvoId");
        const requestParams = {
          customerId: userDetails.customerId,
          logoutTime: new Date().toString(),
        };
        dispatch(logout(requestParams));
      } else {
        alert("Please try again...");
      }
    };
 
    const handleChatClick = () => {
      // Also keep local prop behavior if parent manages chat state
      dispatch(toggleChat());
      if (setIsInsureAssistOpen) {
        setIsInsureAssistOpen(!isInsureAssistOpen);
      }
    };

    const pathSegment = location.pathname.split("/")[2];
    const isNewCustomerDashboard = pathSegment === "newcustomerDashboard";
    const isVoiceForms = pathSegment === "voiceForms";
    const isInsureAssistPage = pathSegment === "insure-assist";

    const headerIconSx = {
      marginRight: "10px",
      fontSize: "24px",
      cursor: "pointer",
    };

    return (
      <>
        <Box
          sx={{
            ...HeaderStyles.headerContainer,
            marginRight: isInsureAssistOpen && chatWidth ? `${chatWidth}px` : 0,
            transition: 'margin-right 0.3s ease',
          }}
          id="customerheader_header-container"
          data-test-id="test-customerheader_header-container"
          aria-label="Header container"
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              img: {
                height: "100%",
                cursor: "pointer",
              },
            }}
            id="customerheader_logo-container"
            data-test-id="test-customerheader_logo-container"
            aria-label="Logo container"
          >
            {uiSettings?.logo ? (
              <img
                src={`data:image/png;base64,${uiSettings.logo}`}
                alt="smart-ims logo"
                onClick={() => navigate("/customer/dashboard")}
                id="customerheader_logo-image"
                data-test-id="test-customerheader_logo-image"
                aria-label="Smart IMS Logo - click to navigate to dashboard"
              />
            ) : null}
          </Box>
          <Box
            sx={{ gap: "10px" }}
            id="customerheader_icon-controls"
            data-test-id="test-customerheader_icon-controls"
            aria-label="Icon controls container"
          >
            {isNewCustomerDashboard || isInsureAssistPage || isVoiceForms ? (
              <DesktopWindowsOutlinedIcon
                sx={headerIconSx}
                onClick={() => {
                  sessionStorage.removeItem("insureAssistPopupMessages");
                  navigate("/customer/Dashboard");
                }}
                id="customerheader_chat-toggle"
                data-test-id="test-customerheader_chat-toggle"
                aria-label="Back to customer dashboard"
              />
            ) : (
              <AutoAwesomeOutlinedIcon
                sx={headerIconSx}
                onClick={() => {
                  sessionStorage.removeItem("chatHeaderMessages");
                  navigate("/customer/newcustomerDashboard");
                }}
                id="customerheader_chat-toggle"
                data-test-id="test-customerheader_chat-toggle"
                aria-label="Open AI assistant"
              />
            )}
            {!isVoiceForms && (
              <RecordVoiceOverOutlinedIcon
                sx={{
                  ...headerIconSx,
                  color: isNewCustomerDashboard ? "#4465b3" : undefined,
                }}
                onClick={() => navigate("/customer/voiceForms")}
                id="customerheader_voice-toggle"
                data-test-id="test-customerheader_voice-toggle"
                aria-label="Open insurance claim voice intake"
              />
            )}
            {showInsureAssist &&
              !isNewCustomerDashboard &&
              !isVoiceForms && (
                <ChatIcon
                  sx={{
                    marginRight: "10px",
                    fontSize: "24px",
                    cursor: "pointer",
                  }}
                  onClick={handleChatClick}
                  id="customerheader_insure-assist-toggle"
                  data-test-id="test-customerheader_insure-assist-toggle"
                  aria-label={
                    isInsureAssistOpen
                      ? "Close InsureAssist"
                      : "Open InsureAssist"
                  }
                />
              )}
            <LanguageIcon
              sx={{ marginRight: "15px", fontSize: "25px", cursor: "pointer" }}
              onClick={handleLanguageClick}
              id="customerheader_language-button"
              data-test-id="test-customerheader_language-button"
              aria-label="Change language"
            />
            <MenuIcon
              sx={{ cursor: "pointer" }}
              onClick={handleMenuClick}
              id="customerheader_menu-button"
              data-test-id="test-customerheader_menu-button"
              aria-label="Open menu"
            />
          </Box>
        </Box>
 
        <Menu
          anchorEl={languageMenu}
          open={languageOpen}
          onClose={handleLanguageClose}
          MenuListProps={{
            "aria-labelledby": "customerheader_language-button",
          }}
          disableScrollLock
          id="customerheader_language-menu"
          data-test-id="test-customerheader_language-menu"
          aria-label="Language selection menu"
        >
          <MenuItem
            selected={i18n.language === "en"}
            onClick={() => handleLanguageSelect("en")}
            id="customerheader_language-en"
            data-test-id="test-customerheader_language-en"
            aria-label="Select English language"
          >
            English
          </MenuItem>
          <MenuItem
            selected={i18n.language === "es"}
            onClick={() => handleLanguageSelect("es")}
            id="customerheader_language-es"
            data-test-id="test-customerheader_language-es"
            aria-label="Select Spanish language"
          >
            Español
          </MenuItem>
        </Menu>
 
        <Menu
          id="customerheader_main-menu"
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={handleMenuClose}
          MenuListProps={{
            "aria-labelledby": "customerheader_menu-button",
          }}
          disableScrollLock
          data-test-id="test-customerheader_main-menu"
          aria-label="Main menu"
        >
          {isFeatureAvailable({ featureCodes: ["profile"], features }) && (
            <MenuItem
              onClick={handleProfileClick}
              id="customerheader_menuitem-profile"
              data-test-id="test-customerheader_menuitem-profile"
              aria-label="Go to Profile"
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: "8px" }}
                id="customerheader_profile-box"
                data-test-id="test-customerheader_profile-box"
                aria-label="Profile box"
              >
                <AccountCircleIcon />
                <Typography>{t("Profile")}</Typography>
              </Box>
            </MenuItem>
          )}
          {isFeatureAvailable({ featureCodes: ["my_tasks"], features }) && (
            <MenuItem
              onClick={handleMyTasksClick}
              id="customerheader_menuitem-mytasks"
              data-test-id="test-customerheader_menuitem-mytasks"
              aria-label="Go to My Tasks"
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: "8px" }}
                id="customerheader_mytasks-box"
                data-test-id="test-customerheader_mytasks-box"
                aria-label="My Tasks box"
              >
                <AssignmentRoundedIcon />
                <Typography>{t("My Tasks")}</Typography>
              </Box>
            </MenuItem>
          )}
          <MenuItem
            onClick={handleLogout}
            id="customerheader_menuitem-logout"
            data-test-id="test-customerheader_menuitem-logout"
            aria-label="Logout from portal"
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: "8px" }}
              id="customerheader_logout-box"
              data-test-id="test-customerheader_logout-box"
              aria-label="Logout box"
            >
              <LogoutIcon />
              <Typography>{t("Logout")}</Typography>
            </Box>
          </MenuItem>
        </Menu>
      </>
    );
  }
);
 
export default Header;
